import React, { useEffect, useState } from 'react'
import { SubmitHandler } from 'react-hook-form'
import { Box, Typography } from 'transfix-ui/core'
import formatPhone from 'utils/formatPhone'
import { useVerifyAccountMutation } from '../graphql/VerifyAccount'
import { useSendSMSVerificationCode } from '../util/useSendSMSVerificationCode'
import { PhoneVerificationCodeForm } from './PhoneVerificationCodeForm/PhoneVerificationCodeForm'
import { TPhoneVerificationCodeSchema } from './PhoneVerificationCodeForm/PhoneVerificationCodeSchema'
import { ProvidePhoneNumberForm } from './ProvidePhoneNumberForm/ProvidePhoneNumberForm'
import { TProvidePhoneNumberSchema } from './ProvidePhoneNumberForm/ProvidePhoneNumberSchema'

interface IVerifyAccountStepProps {
  onCancel: () => void
  /**
   * called when the account was verified
   */
  onVerificationSuccess: () => void
  loading?: boolean
  errorMessage?: string
}

const VerifyAccountWithPhoneNumber = ({
  errorMessage,
  loading,
  onCancel,
  onVerificationSuccess,
}: IVerifyAccountStepProps) => {
  const [codeAlertMessage, setCodeAlertMessage] = useState<string>('')
  const {
    alertMessage: phoneAlertMessage,
    reset,
    sendNewCode,
    success: isVerificationCodeSent,
  } = useSendSMSVerificationCode()
  const [verifyAccountMutation] = useVerifyAccountMutation()

  // resets the state of query parameters on mount
  useEffect(() => reset(), [reset])

  const submitPhoneHandler: SubmitHandler<TProvidePhoneNumberSchema> = ({ phoneNumber }) =>
    sendNewCode({ cellPhone: formatPhone(phoneNumber) })

  const submitCodeHandler: SubmitHandler<TPhoneVerificationCodeSchema> = async ({ code }) => {
    try {
      setCodeAlertMessage('')
      const result = await verifyAccountMutation({ variables: { input: { phoneCode: code } } })

      if (result.errors?.length) {
        throw new Error(result.errors[0].message)
      }
      if (!result.data?.verifyUserAccount?.success) {
        setCodeAlertMessage("Sorry, we didn't recognize that verification code.")
        return
      }

      onVerificationSuccess()
    } catch (e) {
      setCodeAlertMessage('Sorry, something went wrong.')
    }
  }

  return (
    <Box>
      <Box paddingBottom='12px'>
        <Typography variant='body2'>
          Before you permanently delete your account, please enter you mobile phone number.
        </Typography>
      </Box>

      {!isVerificationCodeSent ? (
        <ProvidePhoneNumberForm
          onSubmitSuccess={submitPhoneHandler}
          onCancel={onCancel}
          alertMessage={phoneAlertMessage}
        />
      ) : (
        <PhoneVerificationCodeForm
          onSubmitSuccess={submitCodeHandler}
          onCancel={onCancel}
          alertMessage={codeAlertMessage || errorMessage}
          loading={loading}
        />
      )}
    </Box>
  )
}

export default VerifyAccountWithPhoneNumber
